import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectMedicalData } from "../redux/selectors/medicalSelectors";
import { selectPersonalHistory } from "../redux/selectors/personalHistorySelectors";
import { selectFamilyData } from "../redux/selectors/familyHistorySelectors";
import { selectSurgicalData } from "../redux/selectors/surgicalHistorySelectors";
import { selectBmiHistoryData } from "../redux/selectors/bmiHistorySelectors";
import { selectVaccinationData } from "../redux/selectors/vaccinationSelectors";

import { fetchUserById } from "../redux/thunks/userThunks";
import { fetchMedicalInfoByUserId } from "../redux/thunks/medicalThunks";
import { fetchPersonalHistoryByUserId } from "../redux/thunks/personalHistoryThunks";
import { fetchFamilyHistory } from "../redux/thunks/familyHistoryThunks";
import { fetchSurgicalHistory } from "../redux/thunks/surgicalHistoryThunks";
import { fetchBmiHistoryByUserId } from "../redux/thunks/bmiHistoryThunks";
import { fetchVaccinations } from "../redux/thunks/vaccinationThunks";

/* =========================================================
   GENERIC HELPERS
========================================================= */

const pick = (...values) =>
  values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
  ) ?? null;

const normalizeId = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return (
      value.id ||
      value.userId ||
      value.patientId ||
      value._id ||
      null
    );
  }

  const normalized = String(value).trim();

  return normalized || null;
};

const normalizeObject = (value) => {
  if (!value) return {};

  if (Array.isArray(value)) {
    return value[0] || {};
  }

  if (
    value.data &&
    !Array.isArray(value.data) &&
    typeof value.data === "object"
  ) {
    return value.data;
  }

  return value;
};

const normalizeArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value.data)) {
    return value.data;
  }

  if (Array.isArray(value.items)) {
    return value.items;
  }

  if (Array.isArray(value.results)) {
    return value.results;
  }

  if (Array.isArray(value.history)) {
    return value.history;
  }

  if (Array.isArray(value.records)) {
    return value.records;
  }

  return [];
};

/* =========================================================
   FIND DATA FOR CURRENT PATIENT
========================================================= */

const getByUserId = (
  state,
  sliceName,
  userId
) => {
  if (!state || !sliceName || !userId) {
    return null;
  }

  const slice =
    state[sliceName];

  if (!slice) {
    return null;
  }

  const id =
    String(userId).trim();

  const lowerId =
    id.toLowerCase();

  return (
    slice?.byUserId?.[id] ??
    slice?.byUserId?.[lowerId] ??
    slice?.byPatientId?.[id] ??
    slice?.byPatientId?.[lowerId] ??
    null
  );
};

/* =========================================================
   DATE / AGE
========================================================= */

const calculateAge = (
  dateOfBirth
) => {
  if (!dateOfBirth) {
    return null;
  }

  const birthDate =
    new Date(dateOfBirth);

  if (
    Number.isNaN(
      birthDate.getTime()
    )
  ) {
    return null;
  }

  const today =
    new Date();

  let age =
    today.getFullYear() -
    birthDate.getFullYear();

  const monthDifference =
    today.getMonth() -
    birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 &&
      today.getDate() <
        birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= 0
    ? age
    : null;
};

/* =========================================================
   RECORD DATE
========================================================= */

const getRecordTimestamp = (
  record
) => {
  if (!record) {
    return 0;
  }

  const candidates = [
    record.updatedAt,
    record.createdAt,
    record.addedAt,
    record.recordedAt,
    record.date,
    record.timestamp,
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const timestamp =
      new Date(
        candidate
      ).getTime();

    if (
      !Number.isNaN(
        timestamp
      )
    ) {
      return timestamp;
    }
  }

  return 0;
};

/* =========================================================
   LATEST BMI
========================================================= */

const getLatestRecord = (
  input
) => {
  const list =
    normalizeArray(input);

  if (!list.length) {
    return null;
  }

  const datedRecords =
    list
      .map((item) => ({
        item,
        timestamp:
          getRecordTimestamp(
            item
          ),
      }))
      .filter(
        (item) =>
          item.timestamp > 0
      );

  if (
    datedRecords.length >
    0
  ) {
    datedRecords.sort(
      (a, b) =>
        b.timestamp -
        a.timestamp
    );

    return datedRecords[0]
      .item;
  }

  /*
   * Aucun timestamp :
   * on utilise le premier record.
   */
  return list[0];
};

/* =========================================================
   MEASUREMENT
========================================================= */

const getValueFromKeys = (
  source,
  keys
) => {
  if (!source) {
    return null;
  }

  for (const key of keys) {
    const value =
      source[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return null;
};

const resolveMeasurement = ({
  bmiRecord,
  medical,
  user,
  fallback,
  keys,
}) => {
  const bmiValue =
    getValueFromKeys(
      bmiRecord,
      keys
    );

  const medicalValue =
    getValueFromKeys(
      medical,
      keys
    );

  const userValue =
    getValueFromKeys(
      user,
      keys
    );

  const fallbackValue =
    getValueFromKeys(
      fallback,
      keys
    );

  const bmiTimestamp =
    getRecordTimestamp(
      bmiRecord
    );

  const medicalTimestamp =
    getRecordTimestamp(
      medical
    );

  /*
   * Si BMI et medical ont une date,
   * la valeur la plus récente gagne.
   */
  if (
    bmiValue !== null &&
    medicalValue !== null &&
    bmiTimestamp > 0 &&
    medicalTimestamp > 0
  ) {
    return medicalTimestamp >
      bmiTimestamp
      ? medicalValue
      : bmiValue;
  }

  return pick(
    bmiValue,
    medicalValue,
    userValue,
    fallbackValue
  );
};

/* =========================================================
   HOOK
========================================================= */

export const usePatientProfile = (
  patientData,
  { enabled = true } = {}
) => {
  const dispatch =
    useDispatch();

  const rawPatient =
    patientData?.raw ||
    patientData ||
    {};

  /* =======================================================
     PATIENT ID
  ======================================================= */

  const userId =
    useMemo(
      () =>
        normalizeId(
          patientData?.id ||
            patientData?.userId ||
            rawPatient?.id ||
            rawPatient?.userId ||
            rawPatient?._id
        ),
      [
        patientData,
        rawPatient,
      ]
    );

  const normalizedUserId =
    String(userId || "")
      .trim()
      .toLowerCase();

  /* =======================================================
     USER

     Important :
     on ne prend PAS state.user.current
     car côté doctor cela peut être le mauvais patient.
  ======================================================= */

  const reduxUser =
    useSelector(
      (state) =>
        state.user
          ?.usersById?.[
          normalizedUserId
        ] ||
        state.user
          ?.usersById?.[
          userId
        ] ||
        null
    );

  const userState =
    useSelector(
      (state) =>
        state.user || {}
    );

  /* =======================================================
     EXISTING GENERIC SELECTORS

     Ils restent uniquement comme FALLBACK.
  ======================================================= */

  const fallbackMedical =
    useSelector(
      selectMedicalData
    );

  const fallbackPersonal =
    useSelector(
      selectPersonalHistory
    );

  const fallbackFamily =
    useSelector(
      selectFamilyData
    );

  const fallbackSurgical =
    useSelector(
      selectSurgicalData
    );

  const fallbackBmi =
    useSelector(
      selectBmiHistoryData
    );

  const fallbackVaccinations =
    useSelector(
      selectVaccinationData
    );

  /* =======================================================
     CURRENT PATIENT DATA FROM REDUX

     ✅ priorité au patient ID
  ======================================================= */

  const medicalByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "medicalInfo",
          userId
        ) ||
        getByUserId(
          state,
          "medical",
          userId
        )
    );

  const personalByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "personalHistory",
          userId
        )
    );

  const familyByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "familyHistory",
          userId
        )
    );

  const surgicalByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "surgicalHistory",
          userId
        )
    );

  const bmiByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "bmiHistory",
          userId
        )
    );

  const vaccinationByPatient =
    useSelector(
      (state) =>
        getByUserId(
          state,
          "vaccination",
          userId
        ) ||
        getByUserId(
          state,
          "vaccinations",
          userId
        )
    );

  /* =======================================================
     FINAL SOURCES

     byUserId gagne toujours.
  ======================================================= */

  const medicalData =
    medicalByPatient ??
    fallbackMedical ??
    null;

  const personalHistory =
    personalByPatient ??
    fallbackPersonal ??
    [];

  const familyData =
    familyByPatient ??
    fallbackFamily ??
    [];

  const surgicalData =
    surgicalByPatient ??
    fallbackSurgical ??
    [];

  const bmiHistory =
    bmiByPatient ??
    fallbackBmi ??
    [];

  const vaccinationData =
    vaccinationByPatient ??
    fallbackVaccinations ??
    [];

  /* =======================================================
     LOADING STATES
  ======================================================= */

  const medicalState =
    useSelector(
      (state) =>
        state.medicalInfo ||
        state.medical ||
        {}
    );

  const personalState =
    useSelector(
      (state) =>
        state.personalHistory ||
        {}
    );

  /* =======================================================
     FETCH ALL PATIENT DATA
  ======================================================= */

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    const load =
      async () => {
        const results =
          await Promise.allSettled([
            dispatch(
              fetchUserById(
                userId
              )
            ),

            dispatch(
              fetchMedicalInfoByUserId(
                userId
              )
            ),

            dispatch(
              fetchPersonalHistoryByUserId(
                userId
              )
            ),

            dispatch(
              fetchFamilyHistory(
                userId
              )
            ),

            dispatch(
              fetchSurgicalHistory(
                userId
              )
            ),

            dispatch(
              fetchBmiHistoryByUserId(
                userId
              )
            ),

            dispatch(
              fetchVaccinations(
                userId
              )
            ),
          ]);

        /*
         * Très important pour voir quelle API échoue.
         */
        const names = [
          "USER",
          "MEDICAL",
          "PERSONAL HISTORY",
          "FAMILY HISTORY",
          "SURGICAL HISTORY",
          "BMI HISTORY",
          "VACCINATIONS",
        ];

        results.forEach(
          (
            result,
            index
          ) => {
            if (
              result.status ===
              "rejected"
            ) {
              console.error(
                `❌ ${names[index]} FAILED`,
                result.reason
              );
            }
          }
        );
      };

    load();
  }, [
    dispatch,
    enabled,
    userId,
  ]);

  /* =======================================================
     NORMALIZE MEDICAL
  ======================================================= */

  const medical =
    useMemo(
      () =>
        normalizeObject(
          medicalData
        ),
      [
        medicalData,
      ]
    );

  /* =======================================================
     FRESH USER

     Redux data prend priorité sur patientData.
  ======================================================= */

  const freshUser =
    useMemo(
      () => ({
        ...rawPatient,
        ...(reduxUser ||
          {}),
      }),
      [
        rawPatient,
        reduxUser,
      ]
    );

  /* =======================================================
     NORMALIZED ARRAYS
  ======================================================= */

  const normalizedPersonalHistory =
    useMemo(
      () =>
        normalizeArray(
          personalHistory
        ),
      [
        personalHistory,
      ]
    );

  const normalizedFamilyData =
    useMemo(
      () =>
        normalizeArray(
          familyData
        ),
      [
        familyData,
      ]
    );

  const normalizedSurgicalData =
    useMemo(
      () =>
        normalizeArray(
          surgicalData
        ),
      [
        surgicalData,
      ]
    );

  const normalizedBmiHistory =
    useMemo(
      () =>
        normalizeArray(
          bmiHistory
        ),
      [
        bmiHistory,
      ]
    );

  const normalizedVaccinationData =
    useMemo(
      () =>
        normalizeArray(
          vaccinationData
        ),
      [
        vaccinationData,
      ]
    );

  /* =======================================================
     LATEST BMI
  ======================================================= */

  const latestBmiRecord =
    useMemo(
      () =>
        getLatestRecord(
          normalizedBmiHistory
        ),
      [
        normalizedBmiHistory,
      ]
    );

  /* =======================================================
     DATE OF BIRTH
  ======================================================= */

  const dateOfBirth =
    useMemo(
      () =>
        pick(
          freshUser?.dateOfBirth,
          freshUser?.dateDeNaissance,
          freshUser?.birthDate,
          freshUser?.birthday,
          freshUser?.dob,

          medical?.dateOfBirth,
          medical?.dateDeNaissance,
          medical?.birthDate,
          medical?.birthday,
          medical?.dob,

          patientData?.dateOfBirth,
          patientData?.dateDeNaissance,
          patientData?.birthDate,
          patientData?.birthdate,
          patientData?.dob
        ),
      [
        freshUser,
        medical,
        patientData,
      ]
    );

  /* =======================================================
     AGE
  ======================================================= */

  const age =
    useMemo(() => {
      const directAge =
        pick(
          freshUser?.age,
          medical?.age,
          patientData?.age
        );

      if (
        directAge !== null
      ) {
        const number =
          Number(
            directAge
          );

        if (
          Number.isFinite(
            number
          ) &&
          number >= 0
        ) {
          return Math.floor(
            number
          );
        }
      }

      return calculateAge(
        dateOfBirth
      );
    }, [
      freshUser,
      medical,
      patientData,
      dateOfBirth,
    ]);

  /* =======================================================
     HEIGHT
  ======================================================= */

  const height =
    useMemo(
      () =>
        resolveMeasurement({
          bmiRecord:
            latestBmiRecord,

          medical,

          user:
            freshUser,

          fallback:
            patientData,

          keys: [
            "tailleCm",
            "heightCm",
            "taille",
            "height",
          ],
        }),
      [
        latestBmiRecord,
        medical,
        freshUser,
        patientData,
      ]
    );

  /* =======================================================
     WEIGHT
  ======================================================= */

  const weight =
    useMemo(
      () =>
        resolveMeasurement({
          bmiRecord:
            latestBmiRecord,

          medical,

          user:
            freshUser,

          fallback:
            patientData,

          keys: [
            "poidsKg",
            "weightKg",
            "poids",
            "weight",
          ],
        }),
      [
        latestBmiRecord,
        medical,
        freshUser,
        patientData,
      ]
    );

  /* =======================================================
     FINAL PROFILE
  ======================================================= */

  const profileData =
    useMemo(() => {
      const firstName =
        pick(
          freshUser?.firstName,
          patientData?.firstName
        );

      const lastName =
        pick(
          freshUser?.lastName,
          patientData?.lastName
        );

      const fullName =
        pick(
          freshUser?.fullName,

          firstName ||
            lastName
            ? `${firstName || ""} ${
                lastName || ""
              }`.trim()
            : null,

          freshUser?.name,
          patientData?.name
        );

      return {
        id:
          userId,

        identifier:
          pick(
            freshUser?.identifier,
            patientData?.identifier,
            freshUser?.patientIdentifier,
            patientData?.patientIdentifier
          ),

        patientId:
          pick(
            freshUser?.patientId,
            freshUser?.identifier,
            patientData?.patientId,
            patientData?.identifier
          ),

        firstName,

        lastName,

        fullName,

        phone:
          pick(
            freshUser?.phone,
            freshUser?.phoneNumber,
            patientData?.phone
          ),

        email:
          pick(
            freshUser?.email,
            patientData?.email
          ),

        photo:
          pick(
            freshUser?.photo,
            freshUser?.avatarUrl,
            freshUser?.avatar,
            freshUser?.profilePicture,
            patientData?.photo,
            patientData?.avatarUrl
          ),

        gender:
          pick(
            medical?.sexe,
            medical?.gender,
            freshUser?.gender,
            freshUser?.sex,
            patientData?.gender
          ),

        bloodType:
          pick(
            medical?.groupeSanguin,
            medical?.bloodType,
            medical?.bloodGroup,
            freshUser?.bloodType,
            patientData?.bloodType
          ),

        height,

        weight,

        age,

        dateOfBirth,
      };
    }, [
      userId,
      freshUser,
      patientData,
      medical,
      height,
      weight,
      age,
      dateOfBirth,
    ]);

  /* =======================================================
     DEBUG

     TEMPORAIRE :
     garde ceci maintenant pour voir exactement
     ce que chaque API fournit.
  ======================================================= */

  useEffect(() => {
    if (!userId) {
      return;
    }

    console.group(
      `🏥 PATIENT MEDICAL DATA — ${userId}`
    );

    console.log(
      "👤 USER:",
      reduxUser
    );

    console.log(
      "🩺 MEDICAL:",
      medical
    );

    console.log(
      "⚖️ BMI HISTORY:",
      normalizedBmiHistory
    );

    console.log(
      "🧍 PERSONAL HISTORY:",
      normalizedPersonalHistory
    );

    console.log(
      "⚠️ SURGICAL HISTORY:",
      normalizedSurgicalData
    );

    console.log(
      "👨‍👩‍👧 FAMILY HISTORY:",
      normalizedFamilyData
    );

    console.log(
      "💉 VACCINATIONS:",
      normalizedVaccinationData
    );

    console.log(
      "✅ FINAL PROFILE:",
      profileData
    );

    console.groupEnd();
  }, [
    userId,
    reduxUser,
    medical,
    normalizedBmiHistory,
    normalizedPersonalHistory,
    normalizedSurgicalData,
    normalizedFamilyData,
    normalizedVaccinationData,
    profileData,
  ]);

  /* =======================================================
     LOADING
  ======================================================= */

  const isLoading =
    Boolean(
      userState?.loading
    ) ||
    Boolean(
      medicalState?.loading
    ) ||
    Boolean(
      personalState?.loading
    );

  /* =======================================================
     RETURN
  ======================================================= */

  return {
    userId,

    profileData,

    medicalData:
      medical,

    personalHistory:
      normalizedPersonalHistory,

    familyData:
      normalizedFamilyData,

    surgicalData:
      normalizedSurgicalData,

    bmiHistory:
      normalizedBmiHistory,

    latestBmiRecord,

    vaccinationData:
      normalizedVaccinationData,

    isLoading,
  };
};
